import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Signal,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, finalize } from 'rxjs';

import {
  ClientContextBlock,
  ClientContextEntry,
} from '../../../../core/context/context-blocks.types';
import { guardTypedInput } from '../../../../shared/context-blocks-panel/context-dialog';
import { IconComponent } from '../../../../shared/icon/icon';

export interface ClientAddDraft {
  text: string;
  file?: File;
}

export interface ClientBlockDialogData {
  /** The block as the page holds it now — re-read after every addition. */
  readonly block: Signal<ClientContextBlock | undefined>;
  readonly save: (draft: ClientAddDraft) => Observable<unknown>;
  readonly openFile: (entry: ClientContextEntry) => void;
}

/**
 * The business's pop-up for one block (DOC-89-A §3, UI-DA-89-A-03; FLOW-401):
 * the entries, each with the server's own words for where it came from
 * (*From you*, *From you — filed by CapX*, *From CapX*) and a date; *Add to
 * this* (a note, a document) — **the add form opens inside the pop-up, never
 * inside the page**; *Close*. Closes on Close, Escape or a press outside, and
 * asks *Discard what you typed?* before losing anything typed.
 *
 * 🚨 No count, no tick, no colour, no state word — nothing here asks for
 * anything (DOC-59; DOC-73 `UI-DA-73-03`). Where an addition came from is the
 * server's to record, never the business's to choose.
 */
@Component({
  selector: 'app-client-block-dialog',
  imports: [
    DatePipe,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatTooltipModule,
    IconComponent,
  ],
  templateUrl: './client-block-dialog.html',
  styleUrl: './client-block-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientBlockDialogComponent {
  protected readonly data = inject<ClientBlockDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject<MatDialogRef<ClientBlockDialogComponent>>(MatDialogRef);
  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  protected readonly adding = signal(false);
  protected readonly text = signal('');
  protected readonly file = signal<File | null>(null);
  protected readonly busy = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly canSave = computed(
    () => !this.busy() && (!!this.text().trim() || !!this.file()),
  );

  /** Close, Escape and a press outside ask *Discard what you typed?* once anything is typed. */
  protected readonly close = guardTypedInput(
    this.dialogRef,
    inject(MatDialog),
    () => this.adding() && (!!this.text().trim() || !!this.file()),
  );

  constructor() {
    effect(() => {
      if (this.data.block() === undefined) this.dialogRef.close();
    });
  }

  protected openAdd(): void {
    this.adding.set(true);
    this.error.set(null);
  }

  protected cancelAdd(): void {
    this.adding.set(false);
    this.text.set('');
    this.file.set(null);
    const el = this.fileInput()?.nativeElement;
    if (el) el.value = '';
  }

  protected onFile(event: Event): void {
    this.file.set((event.target as HTMLInputElement).files?.[0] ?? null);
  }

  protected save(): void {
    if (!this.canSave()) return;
    this.busy.set(true);
    this.error.set(null);
    this.data
      .save({ text: this.text().trim(), file: this.file() ?? undefined })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: () => this.cancelAdd(),
        error: (err: unknown) => {
          const detail = (err as { error?: { detail?: unknown } })?.error?.detail;
          this.error.set(
            typeof detail === 'string' ? detail : 'That could not be added. Please try again.',
          );
        },
      });
  }
}
